// Routing and the session gate. Signed out: the landing page (at any URL, returning there after
// sign-in). Signed in with no workspace: the empty state. Otherwise the board, records, the fleet
// (owners) and admin.
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { api } from './api/client';
import { MAIN } from './components/Shell';
import { Callout, cn } from './components/ui';
import { SessionContext, findWorkspace, lastWorkspace } from './lib/session';
import { boardPath } from './lib/routes';
import { useLoad } from './lib/useLoad';
import { AdminPage } from './pages/AdminPage';
import { BoardPage } from './pages/BoardPage';
import { EmptyState } from './pages/EmptyState';
import { FleetPage } from './pages/FleetPage';
import { Landing } from './pages/Landing';
import { NotFoundPage } from './pages/NotFoundPage';
import { RepoSettingsPage } from './pages/RepoSettingsPage';
import { RecordPage } from './pages/RecordPage';
import { StyleGuide } from './pages/StyleGuide';

function Gate() {
  const location = useLocation();
  const [me, reload] = useLoad(() => api.GET('/api/me'), 'me');
  if (location.pathname.replace(/\/+$/, '') === '/style-guide') return <StyleGuide />;
  if (me.state === 'loading') {
    return (
      <p className="px-5 py-4 text-ink-muted" aria-busy="true">
        Loading…
      </p>
    );
  }
  if (me.state === 'error') {
    if (me.status === 401) return <Landing next={location.pathname + location.search} />;
    return (
      <main className={cn(MAIN, 'mx-auto max-w-[48rem]')}>
        <Callout as="p" role="alert">
          {me.message}
        </Callout>
      </main>
    );
  }
  const session = { me: me.data, reload };
  if (me.data.workspaces.length === 0) return <EmptyState me={me.data} />;
  const last = lastWorkspace();
  const home = findWorkspace(me.data, last ?? -1) ?? me.data.workspaces[0];
  return (
    <SessionContext.Provider value={session}>
      <Routes>
        <Route path="/" element={home ? <Navigate to={boardPath(home.id)} replace /> : <NotFoundPage />} />
        <Route path="/w/:ws" element={<WsRedirect />} />
        <Route path="/w/:ws/board" element={<BoardPage />} />
        <Route path="/w/:ws/repos/:repo/records/:key" element={<RecordPage />} />
        <Route path="/w/:ws/fleet" element={<FleetPage />} />
        <Route path="/w/:ws/settings/repos/:repo" element={<RepoSettingsPage />} />
        <Route path="/w/:ws/settings" element={<AdminPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </SessionContext.Provider>
  );
}

function WsRedirect() {
  const { pathname } = useLocation();
  return <Navigate to={`${pathname.replace(/\/+$/, '')}/board`} replace />;
}

export function App() {
  return (
    <BrowserRouter>
      <Gate />
    </BrowserRouter>
  );
}
